import { ArrowRight, Check, Star, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import type { RebookingOption } from '@/lib/rebooking/types';
import { formatFlightClock, formatFlightDay, formatFlightTime } from '@/lib/disruptions/airport-tz';
import { fmtDuration, journeyOf, layoverLabel } from '@/routes/disruptions/pnr-status';
import { formatPriceUsd } from '@/lib/rebooking/price';
import { flightProviderName, isNexaBoughtProvider, optionDeparted } from '@/lib/rebooking/flight-provider';
import { BoardChip } from './airport-ui';

// One ranked rebooking option, rendered as a DEPARTURE-BOARD ROW: the times set
// big and tabular on the left (with the arrival's day marked +1 when the option
// lands the next day — the thing an operator must not miss), the flight and its
// route in mono next to them, the price on the right rail. The whole row IS the
// select control (no separate button) and marks itself selected.
//
// A direct flight collapses to that single line; a real connection keeps a
// journey summary + the per-leg breakdown with layovers, since there the summary
// and the legs carry different information.

interface PnrOptionRowProps {
  option: RebookingOption;
  recommended: boolean;
  selected: boolean;
  pending: boolean;
  /** The group is ticketed (committed in the PSS) — the row stays visible for
   *  reference but is never selectable, with no selectable affordance. */
  locked?: boolean;
  onSelect: () => void;
}

/** Departure → arrival, board-sized, with the day under each and a +1 marker when
 *  the option arrives on a later calendar day at the arrival airport. */
function TimeBlock({
  depIso,
  depIata,
  arrIso,
  arrIata,
}: {
  depIso: string;
  depIata?: string | undefined;
  arrIso: string;
  arrIata?: string | undefined;
}) {
  const depDay = formatFlightDay(depIso, depIata);
  const arrDay = formatFlightDay(arrIso, arrIata);
  // The day is said ONCE under the departure; the arrival repeats it only when
  // the option actually lands on another day — then it is the point, not noise.
  const nextDay = depDay !== arrDay;
  return (
    <div className="flex shrink-0 items-start gap-2">
      <div className="text-left">
        <div className="text-base font-bold leading-none tabular-nums">
          {formatFlightClock(depIso, depIata)}
        </div>
        <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">{depDay}</div>
      </div>
      <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/70" aria-hidden />
      <div className="text-left">
        <div className="flex items-start gap-1 text-base font-bold leading-none tabular-nums">
          {formatFlightClock(arrIso, arrIata)}
          {nextDay ? (
            <span className="text-[10px] font-bold leading-none text-amber-600 dark:text-amber-400">
              +1
            </span>
          ) : null}
        </div>
        {nextDay ? (
          <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
            {arrDay}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** One ranked rebooking option as a clickable row inside the PNR dialog. */
export function PnrOptionRow({
  option,
  recommended,
  selected,
  pending,
  locked = false,
  onSelect,
}: PnrOptionRowProps) {
  const { t } = useTranslation('pnr');
  const journey = journeyOf(option.segments);
  const direct = option.segments.length <= 1;
  const seg0 = option.segments[0];
  const segLast = option.segments[option.segments.length - 1];
  // A flight that already left (a stale search) is shown for reference, never chosen.
  const departed = !selected && optionDeparted(option);

  return (
    <li data-testid="drawer-option" data-departed={departed || undefined}>
      <button
        type="button"
        onClick={onSelect}
        disabled={pending || selected || locked || departed}
        aria-pressed={selected}
        data-testid="drawer-select-option"
        className={cn(
          'group flex w-full items-center gap-3.5 px-3.5 py-3 text-left transition-colors',
          'disabled:cursor-default',
          !locked && !departed && 'hover:bg-muted/60',
          departed && 'opacity-60',
          selected && 'bg-emerald-50 dark:bg-emerald-950/40',
          recommended && !selected && !departed && 'bg-primary/[0.04]',
        )}
      >
        {/* The row is the control — show what a click does (radio → check). */}
        <span
          className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
            selected
              ? 'border-emerald-500 bg-emerald-500 text-white'
              : cn('border-muted-foreground/40', !locked && 'group-hover:border-primary'),
          )}
          aria-hidden
        >
          {selected ? <Check className="h-3 w-3" /> : null}
        </span>

        {seg0 && segLast ? (
          <TimeBlock
            depIso={seg0.departure}
            depIata={seg0.origin}
            arrIso={segLast.arrival}
            arrIata={segLast.destination}
          />
        ) : null}

        <div className="min-w-0 flex-1">
          {direct && seg0 ? (
            /* DIRECT — one line; the route is shown once, not repeated. */
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <span className="font-mono font-bold">{seg0.flightNumber}</span>
              <span className="font-mono text-muted-foreground">
                {seg0.origin} → {seg0.destination}
              </span>
              {journey.totalLabel ? (
                <span className="text-xs tabular-nums text-muted-foreground">
                  {journey.totalLabel}
                </span>
              ) : null}
              {option.protectToFinal && option.onwardDestination ? (
                <BoardChip
                  tone="info"
                  title={t('option.protectTitle', {
                    destination: option.onwardDestination,
                    via: option.onwardOrigin ?? seg0.destination,
                  })}
                >
                  {t('option.protectChip', {
                    destination: option.onwardDestination,
                    via: option.onwardOrigin ?? seg0.destination,
                  })}
                </BoardChip>
              ) : null}
            </div>
          ) : (
            /* CONNECTION — journey summary + per-leg detail (summary ≠ legs). */
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                <span className="font-mono font-bold">
                  {journey.from} → {journey.to}
                </span>
                <BoardChip
                  tone="info"
                  title={t('option.connectionTitle', { via: journey.via.join(', ') })}
                >
                  {t('option.stopsVia', {
                    count: journey.stops,
                    via: journey.via.join(', '),
                  })}
                </BoardChip>
                {/* Another airline's leg: on a flight Nexa buys (Duffel / Atlas) it is a
                    purchase, not interline — no partner agreement is involved. */}
                {journey.anyInterline ? (
                  isNexaBoughtProvider(option.provider) ? (
                    <BoardChip tone="warn" title={t('option.otherCarrierTitle')}>
                      {t('option.otherCarrier')}
                    </BoardChip>
                  ) : (
                    <BoardChip tone="warn" title={t('option.interlineTitle')}>
                      {t('option.interline')}
                    </BoardChip>
                  )
                ) : null}
                {journey.totalLabel ? (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {t('option.totalSuffix', { total: journey.totalLabel })}
                  </span>
                ) : null}
              </div>
              <div className="text-xs text-muted-foreground">
                {option.segments.map((seg, i) => {
                  const next = option.segments[i + 1];
                  return (
                    <div key={`${seg.flightNumber}-${i}`}>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono font-medium">{seg.flightNumber}</span>
                        <span className="font-mono">
                          {seg.origin} → {seg.destination}
                        </span>
                        <span className="tabular-nums">
                          {formatFlightClock(seg.departure, seg.origin)}–
                          {formatFlightClock(seg.arrival, seg.destination)}
                        </span>
                      </div>
                      {next ? (
                        <div className="text-[11px] text-amber-700 dark:text-amber-400">
                          ↳{' '}
                          {t('option.legLayover', {
                            layover: layoverLabel(seg, next),
                            station: seg.destination,
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {option.protectToFinal ? <OptionOnwardLine option={option} /> : null}
          {option.provider ? <OptionProviderLine option={option} /> : null}
        </div>

        {/* Right rail: price + the rank / recommended / selected status. */}
        <div className="shrink-0 text-right">
          {/* The airline's price for the option (commercial-contract §6.13) — never Nexa's
              cost; «No disponible» when the price card is unavailable. */}
          <div className="text-base font-bold leading-none tabular-nums" data-testid="option-price">
            {formatPriceUsd(option.priceBreakdown.totalUsd) ?? (
              <span className="text-xs font-medium text-muted-foreground">{t('option.priceUnavailable')}</span>
            )}
          </div>
          {/* "Est. cost" label removed per issue #747 for consistency with PR #738
              which removed the misleading "COSTO EST." from the PNR cost summary. */}
          <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.1em]">
            {departed ? (
              <span className="text-destructive" data-testid="option-departed">{t('option.departed')}</span>
            ) : selected && optionDeparted(option) ? (
              // The chosen flight left with no ticket (PO 2026-11-03): never a green «Seleccionada».
              <span className="text-destructive" data-testid="option-selected-departed">
                {t('option.selectedDeparted')}
              </span>
            ) : selected ? (
              <span className="text-emerald-600 dark:text-emerald-400">{t('option.selected')}</span>
            ) : recommended ? (
              <span className="inline-flex items-center gap-0.5 text-primary">
                <Star className="h-3 w-3 fill-current" aria-hidden /> {t('option.best')}
              </span>
            ) : (
              <span className="text-muted-foreground">#{option.rank}</span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

/** «Ofrecido por Duffel» — WHO sells this seat (contract `airline-air-ticketing.v1` §3:
 *  hold and ticket go back to that provider), named the way Proveedores names it. Never the
 *  provider's fare: that is what Nexa pays, and the airline sees only the price on the right
 *  rail (commercial-contract §6.13). Pre-proxy options carry no provider: no line. */
function OptionProviderLine({ option }: { option: RebookingOption }) {
  const { t } = useTranslation('pnr');
  const { t: tNetwork } = useTranslation('network');
  return (
    <div className="mt-1 text-[11px] text-muted-foreground" data-testid="option-provider">
      {t('option.offeredBy', { provider: flightProviderName(option.provider ?? '', tNetwork) })}
    </div>
  );
}

/** "↳ then LA1051 09:40 · 7h48m layover ✓/✗" — does THIS option still make the PNR's
 *  surviving onward leg? The question the operator was left to work out by hand. */
function OptionOnwardLine({ option }: { option: RebookingOption }) {
  const { t } = useTranslation('pnr');
  const when = option.onwardDepartureUtc
    ? formatFlightTime(option.onwardDepartureUtc, option.onwardOrigin)
    : undefined;
  const layover =
    typeof option.layoverMinutes === 'number' ? fmtDuration(option.layoverMinutes * 60_000) : undefined;
  const flight = option.onwardFlightNumber ?? t('option.onwardFallback');
  const ok = option.connectionFeasible;
  return (
    <div
      className={cn(
        'mt-1 flex flex-wrap items-center gap-1 text-[11px]',
        ok === undefined
          ? 'text-muted-foreground'
          : ok
            ? 'text-emerald-700 dark:text-emerald-400'
            : 'text-rose-600 dark:text-rose-400',
      )}
      data-testid="option-onward"
    >
      <span aria-hidden>↳</span>
      <span>
        {t('option.then')} <span className="font-mono">{flight}</span>
        {when ? ` ${when}` : ''}
        {layover ? t('option.layoverSuffix', { layover }) : ''}
      </span>
      {ok === undefined ? null : ok ? (
        <Check className="h-3 w-3" aria-hidden />
      ) : (
        <span className="inline-flex items-center gap-0.5">
          <TriangleAlert className="h-3 w-3" aria-hidden />
          {option.overnightAtHub ? t('option.overnightAtHub') : t('option.mayMiss')}
        </span>
      )}
    </div>
  );
}
